import { get } from "@/shared/utils/fetchApi";

const FILTER_ALLOWLIST = new Set(["type", "category", "is_core", "name"]);

/**
 * Serviço para gerenciar definições de nós
 * Implementa o padrão Facade para encapsular as chamadas à API relacionadas a definições de nós
 */
export class NodeDefinitionService {
  private static BASE_URL = "/nodes";

  /**
   * Obtém a definição de um nó pelo ID
   * @param nodeId ID do nó
   * @returns Status da operação no formato { data, error }
   */
  static async getById(
    nodeId: string,
  ): Promise<{ data: Record<string, unknown> | null; error: Error | null }> {
    try {
      const row = await get<Record<string, unknown>>(
        `${this.BASE_URL}/${nodeId}`,
      );
      return { data: row ?? null, error: null };
    } catch (error) {
      // O erro volta no retorno (`{data, error}`), então quem chamou decide o que
      // reportar; logar aqui também era registro em dobro.
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Busca nós com base em filtros.
   * GET /nodes com allowlist de filtros (type/category/is_core/name) e paginação
   * page/page_size — qualquer outra chave é descartada.
   */
  static async getByFilters(
    filters: Record<string, unknown>,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<{ data: Record<string, unknown>[] | null; error: Error | null }> {
    try {
      const queryParams = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) {
        if (FILTER_ALLOWLIST.has(key)) {
          queryParams.append(key, String(value));
        }
      }
      queryParams.append("page", page.toString());
      queryParams.append("page_size", pageSize.toString());
      const result = await get<{
        data: Record<string, unknown>[];
        pagination: unknown;
      }>(`${this.BASE_URL}?${queryParams.toString()}`);
      return { data: result.data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Lista todos os nós, com opção de filtrar por tipo, categoria e se é core
   */
  static async getAll(
    type?: string,
    category?: string,
    isCore?: boolean,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<{ data: Record<string, unknown>[] | null; error: Error | null }> {
    try {
      const queryParams = new URLSearchParams();
      if (type) queryParams.append("type", type);
      if (category) queryParams.append("category", category);
      if (isCore !== undefined)
        queryParams.append("is_core", isCore.toString());
      queryParams.append("page", page.toString());

      queryParams.append("page_size", pageSize.toString());
      const result = await get<{
        data: Record<string, unknown>[];
        pagination: unknown;
      }>(`${this.BASE_URL}?${queryParams.toString()}`);
      return { data: result.data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }
}
